<template>
  <div id="authentication-code-container">
    <div class="tabs-bar">
      <TabsBar
        :tabNames="[$t('authenticationCode.contactType.email'), $t('authenticationCode.contactType.sms')]"
        :getCurrentTab="getActiveIndex"
        :defaultIndex="0"
        :disabledTabs="noSMS ? [$t('authenticationCode.contactType.sms')]: []"
        :disableSwitch="ifStartCountDown"
      />
    </div>
    <div class="content">
      <div>
        <label class="field-label">{{ $t(`authenticationCode.contactType.${contactType}`) }}</label>
        <div class="flex-field">
          <div>
            <p class="content">
              {{ contactAddress }}
            </p>
            <p class="message">
              {{ $t(`authenticationCode.message.${contactType}.${sendCodeStatus}`, [codeExpireInMin]) }}
            </p>
          </div>
          <div class="action-button">
            <button
              class="btn action-btn btn--darkblue"
              :disabled="currentSendCodeStatus === 1 || currentSendCodeStatus === -1"
              @click="onSendButtonClick"
            >
              {{ $t(`authenticationCode.button.${sendCodeStatus}`) }}
              <span v-show="currentSendCodeStatus === 1">(<CountDown
                :trigger="ifStartCountDown"
                :onStart="onCountDownStart"
                :onEnd="onCountDownEnd"
                :defaultStartIndex="60"
              />s)</span>
            </button>
          </div>
        </div>
      </div>
      <div>
        <label class="field-label">{{ $t('authenticationCode.base.verificationCode') }}</label>
        <div class="flex-field">
          <div>
            <div class="code-input">
              <InputField
                @input="onInputChange"
              />
            </div>
          </div>
          <div class="action-button">
            <button
              class="btn action-btn btn--orange"
              :disabled="!canClickLoginButton"
              @click="onLoginButtonClick"
            >
              {{ $t('login') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import lodash from 'lodash'
import { unref, computed } from 'vue-demi'
import TabsBar from '@/controls/TabsBar'
import InputField from '@/controls/InputField'
import CountDown from './CountDown'

import { sendAuthenticationCode } from '@/api/auth/request'
import { useStoreGetters } from '@/store/index'

const SEND_CODE_STATUS = {
  LOCK: -1,
  DEFAULT: 0,
  SENT: 1,
  RESEND: 2,
}

const SEND_CODE_STATUS_TYPES = ['initial', 'waiting', 'resend']
const CONTACT_TYPES = ['email', 'sms']

const replaceStars = (string, startPosition = 2, endPosition = 2) => {
  let newString
  const stringLength = string.length
  const minLength = startPosition + endPosition
  if (stringLength > minLength) {
    newString = lodash.replace(string, string.substr(startPosition, stringLength - minLength), lodash.repeat('*', stringLength - minLength))
  } else {
    newString = lodash.replace(string, string.substr(1, stringLength - 2), lodash.repeat('*', stringLength - 2))
  }
  return newString
}

const hideEmail = (email) => {
  const [address, domain] = lodash.split(email, '@', 2)
  const newAddress = replaceStars(address)
  const domains = lodash.split(domain, '.')
  domains[0] = replaceStars(domains[0])
  const result = lodash.join([newAddress, lodash.join(domains, '.')], '@')
  return result
}

const hideSMS = (sms) => {
  let result
  const hasNationalCode = lodash.startsWith(sms, '+')
  if (hasNationalCode) {
    result = replaceStars(sms, sms.indexOf(' ') + 2, 1)
  } else {
    result = replaceStars(sms, 1)
  }
  return result
}

export default {
  name: 'AuthenticationCodeForm',
  components: {
    TabsBar,
    InputField,
    CountDown,
  },
  props: {
    onLogin: {
      type: Function,
      default: null,
    },
    maxGetCodeCount: {
      type: Number,
      default: 5,
    },
    contactInfo: {
      type: Object,
      default: null,
    },
  },
  setup () {
    const { backEndEnv } = useStoreGetters('settings', ['backEndEnv'])
    const codeExpireInMin = computed(() => {
      return unref(backEndEnv)['security.login.authenticationCodeExpireSec'] / 60
    })
    return {
      codeExpireInMin,
    }
  },
  data () {
    return {
      statusCache: [],
      currentContactTypeIndex: 0,
      currentUserInfo: null,
      noSMS: false,
      ifStartCountDown: false,

      currentSendCodeStatus: SEND_CODE_STATUS.DEFAULT,
      sentCount: 0,
      inputValue: '',
    }
  },
  computed: {
    contactType () {
      const result = CONTACT_TYPES[this.currentContactTypeIndex]
      return result
    },
    sendCodeStatus () {
      return SEND_CODE_STATUS_TYPES[this.currentSendCodeStatus] || SEND_CODE_STATUS_TYPES[0]
    },
    contactAddress () {
      const address = lodash.get(this.currentUserInfo, [this.contactType], '')
      let result

      if (address && this.currentContactTypeIndex === 0) {
        result = hideEmail(address)
      } else if (address && this.currentContactTypeIndex === 1) {
        result = hideSMS(address.replace('|', ' '))
      } else {
        result = ''
      }

      return result
    },
    canClickLoginButton () {
      return this.inputValue.length === 6
    },
  },
  watch: {
    currentContactTypeIndex (index, oldIndex) {
      this.statusCache[oldIndex] = this.getStatus()
      this.setStatus(this.statusCache[index])
    },
    contactInfo (value) {
      if (lodash.isPlainObject(value)) {
        const email = lodash.get(value, ['email'])
        const sms = lodash.get(value, ['sms'])
        const id = lodash.get(value, ['id'])

        if (this.isInvalidSms(sms)) {
          this.noSMS = true
        } else {
          this.noSMS = false
        }

        this.currentUserInfo = {
          email,
          sms,
          id,
        }
      } else {
        this.currentSendCodeStatus = SEND_CODE_STATUS.LOCK
      }
    },
  },
  methods: {
    getActiveIndex (index) {
      if (!this.ifStartCountDown) {
        this.currentContactTypeIndex = index
      }
    },
    isInvalidSms (sms) {
      return lodash.isUndefined(sms) || !sms.split('|')[1]
    },
    getStatus () {
      return {
        currentSendCodeStatus: this.currentSendCodeStatus,
        sentCount: this.sentCount,
        inputValue: this.inputValue,
      }
    },
    setStatus (status) {
      const currentSendCodeStatusCache = lodash.get(status, ['currentSendCodeStatus'])
      const sentCountCache = lodash.get(status, ['sentCount'], 0)
      this.sentCount = sentCountCache
      this.inputValue = lodash.get(status, ['inputValue'], '')
      if (!this.ifStartCountDown && currentSendCodeStatusCache === SEND_CODE_STATUS.DEFAULT) {
        this.currentSendCodeStatus = SEND_CODE_STATUS.DEFAULT
      } else if (!this.ifStartCountDown && currentSendCodeStatusCache === SEND_CODE_STATUS.SENT && sentCountCache < this.maxGetCodeCount) {
        this.currentSendCodeStatus = SEND_CODE_STATUS.RESEND
      } else if (!this.ifStartCountDown && currentSendCodeStatusCache === SEND_CODE_STATUS.RESEND && sentCountCache < this.maxGetCodeCount) {
        this.currentSendCodeStatus = SEND_CODE_STATUS.RESEND
      } else {
        this.currentSendCodeStatus = currentSendCodeStatusCache
      }
    },
    async onSendButtonClick () {
      this.ifStartCountDown = true
      const sentCount = this.sentCount
      if (sentCount < this.maxGetCodeCount) {
        this.sentCount = sentCount + 1
        await sendAuthenticationCode(this.currentUserInfo.id, this.contactType)
      }
    },
    async onLoginButtonClick () {
      if (typeof this.onLogin === 'function') {
        await this.onLogin(this.inputValue)
      }
    },
    onInputChange (value) {
      this.inputValue = value.trim()
    },
    onCountDownStart () {
      this.currentSendCodeStatus = SEND_CODE_STATUS.SENT
    },
    onCountDownEnd () {
      if (this.sentCount >= this.maxGetCodeCount) {
        this.currentSendCodeStatus = SEND_CODE_STATUS.LOCK
      } else {
        this.currentSendCodeStatus = SEND_CODE_STATUS.RESEND
      }
      this.ifStartCountDown = false
    },
  },
}
</script>

<style lang='scss' scoped>

  #authentication-code-container {
    max-width: 100%;

    .content {
      margin-top: 15px;
      background-color: #ffffff;

      & > div {
        padding: 30px 25px;

        &:first-child {
          padding-bottom: 0;
        }
      }
    }

    .field-label {
      font-weight: 700;
      margin-bottom: 12px
    }

    .flex-field {
      display: flex;

      & > div {
        flex: 1;
      }

      & > div:first-child {
        flex: 2;
      }

      & .action-button {
        text-align: right;

        & > button {
          width: 150px;
        }
      }

      & .code-input {

        width: 200px;
      }
    }
  }
</style>
